// Fleet → Device ledger.
//
// The life of one device, from ten perspectives, on ONE axis.
//
// Why one axis and not ten tabs: the questions that matter are causal and
// cross-perspective. "It went quiet on the 14th" is answered by a
// disconnect next to a command next to an app update — three lanes, one
// minute apart. Tabs hide exactly the adjacency that carries the answer.
//
// Two shapes share the axis. An EVENT is an instant (a command, an auth
// attempt); an EPISODE is a span (a ride, a trip, a day of plays) and shows
// its duration. Pretending a 40-minute ride is an instant would be lying by
// omission.
//
// Every row names the table it came from. A single source of truth does not
// mean one table holds everything — it means no claim is unattributable.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronDown, AlertTriangle } from 'lucide-react';
import { PageHead, Pill, Loading, ErrorState, Empty, Button } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import Commands from './Commands';
import Periods from './Periods';
import s from './Ledger.module.css';

// Lane colours are assignments, not decoration: each lane keeps its hue
// across the rail, the chip and the label so the eye can follow one
// perspective down a mixed axis.
const LANE_COLOR = {
  lifecycle:  'var(--iris)',
  trust:      'var(--grow)',
  ops:        '#7c3aed',
  link:       'var(--focus)',
  assignment: '#0891b2',
  software:   '#7e22ce',
  audience:   '#c2410c',
  mobility:   'var(--pend)',
  commercial: '#b45309',
  speech:     '#be185d',
};

const WINDOWS = [7, 14, 30, 90, 180];

// ONE CALENDAR. The server stamps every event with `day` in IST (the bucket
// billing and Brands Manager already use) and names the zone in `timezone`.
// Times render in that same zone rather than the browser's, so grouping,
// heading and clock can never disagree — they did: a UTC group under a
// local-date heading produced two "Sat, 29 Aug 2026" sections, one of which
// held every campaign row and the other none.
const TZ = 'Asia/Kolkata';
const fmtTime = (t, tz = TZ) => new Date(t).toLocaleTimeString('en-GB',
  { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz });
// `day` is already a YYYY-MM-DD in the ledger's zone; render it without
// re-parsing through a timezone that would shift it back.
const fmtDayKey = (day) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined,
    { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

function duration(a, b) {
  if (!b) return null;
  const mins = Math.round((new Date(b) - new Date(a)) / 60000);
  if (mins < 1) return '<1 min';
  if (mins < 90) return `${mins} min`;
  const h = Math.floor(mins / 60);
  return h < 48 ? `${h} h ${mins % 60} min` : `${Math.floor(h / 24)} d`;
}

// Never a bare "m" — beside km figures it reads as metres, and was.
const hhmm = (sec) => {
  if (!sec) return '0 min';
  const h = Math.floor(sec / 3600); const m = Math.round((sec % 3600) / 60);
  return h ? `${h} h ${m} min` : `${m} min`;
};

export default function Ledger() {
  const { thingName } = useParams();
  const [days, setDays] = useState(14);
  const [off, setOff] = useState(() => new Set());
  // Days start CLOSED and their rows are not fetched until one is opened.
  // The page therefore loads the reconciliation table and nothing else: the
  // summary endpoint returns ~20 KB in ~0.5 s, where fetching every lane's
  // rows up front was ~264 KB in ~3 s to render none of them.
  const [open, setOpen] = useState(() => new Set());
  // day -> { loading, events, error }
  const [dayRows, setDayRows] = useState(() => ({}));

  const { data, error, loading, reload } = useApi(
    () => api.get(`/ggos-devices/${encodeURIComponent(thingName)}/ledger`, { params: { days } })
            .then((r) => r.data?.data),
    [thingName, days]);

  // Changing the window invalidates anything already fetched, so a day cannot
  // show rows selected under a different range than its own summary.
  useEffect(() => { setDayRows({}); setOpen(new Set()); }, [thingName, days]);

  const loadDay = useCallback(async (day) => {
    setDayRows((prev) => (prev[day] ? prev : { ...prev, [day]: { loading: true } }));
    try {
      const r = await api.get(
        `/ggos-devices/${encodeURIComponent(thingName)}/ledger/day/${day}`);
      const d = r.data?.data;
      setDayRows((prev) => ({ ...prev, [day]: { events: d?.events || [], degraded: d?.degraded || [] } }));
    } catch (e) {
      setDayRows((prev) => ({ ...prev, [day]: {
        error: e?.response?.data?.error || e?.message || 'Could not load this day' } }));
    }
  }, [thingName]);

  // The fetch is decided OUTSIDE the state updater. Calling it inside meant a
  // side effect in an updater, which React must be free to invoke more than
  // once — StrictMode duly fired every request twice. The symptom is
  // dev-only; the anti-pattern was not.
  const toggleDay = useCallback((day) => {
    const willOpen = !open.has(day);
    setOpen((prev) => {
      const next = new Set(prev);
      if (willOpen) next.add(day); else next.delete(day);
      return next;
    });
    if (willOpen && !dayRows[day]) loadDay(day);
  }, [open, dayRows, loadDay]);

  // The day LIST comes from the summaries — every day with any activity —
  // so a day appears even before its rows are fetched.
  const dayList = useMemo(() => (data?.days || []).map((d) => d.day), [data]);

  const head = (
    <PageHead
      title="Device ledger"
      sub="Everything that happened to this device, from every perspective, on one axis. Times and days are IST — the calendar billing and Brands Manager use. Each row names the table it came from."
      actions={
        <>
          {WINDOWS.map((w) => (
            <Button key={w} primary={w === days} onClick={() => setDays(w)}>{w}d</Button>
          ))}
          <Button onClick={reload}>Refresh</Button>
        </>
      }
    />
  );

  if (loading) return <>{head}<Loading what={`the ledger for ${thingName}`} /></>;
  if (error) return <>{head}<ErrorState error={error} onRetry={reload} /></>;
  if (!data) return <>{head}<Empty title="No ledger" /></>;

  const { device, lanes, counts, degraded, cappedAt } = data;
  const summaryByDay = new Map((data.days || []).map((d) => [d.day, d]));
  const tz = data.timezone || TZ;
  const shownDays = dayList.length;
  const cappedLanes = Object.entries(counts).filter(([, n]) => n === cappedAt).map(([k]) => k);

  const toggle = (lane) => setOff((prev) => {
    const next = new Set(prev);
    if (next.has(lane)) next.delete(lane); else next.add(lane);
    return next;
  });

  return (
    <>
      {head}

      <div className={s.head}>
        <div className={s.ident}>
          <div className={s.thing}>
            <Link to={`/ggos-devices/${thingName}`} style={{ color: 'var(--iris-text)' }}>{device.thingName}</Link>
            <span>·</span><span>{device.model || 'unknown model'}</span>
            {device.osVersion && <><span>·</span><span>OS {device.osVersion}</span></>}
            {device.isTest && <Pill tone="pend">test device</Pill>}
            {!device.active && <Pill tone="bad">deactivated</Pill>}
            {device.requiresAdminReview && <Pill tone="warn">awaiting admission</Pill>}
          </div>
        </div>
      </div>

      {data.state && <DeviceState st={data.state} />}

      {/* Controls sit under the state they act on: you can see the brightness
          before you change it, and whether a passenger is aboard before you
          reboot. Issuing one refetches the open day, so the command lands in
          the Operations lane below with everything it caused. */}
      <Commands thingName={thingName} state={data.state}
                onIssued={() => { const d = [...open][0]; if (d) loadDay(d); }} />

      {/* Loaded on its own so a ninety-day comparison never delays the page. */}
      <Periods thingName={thingName} />

      {device.milestones?.length > 0 && (
        <div className={s.miles}>
          {device.milestones.map((m) => (
            <div key={m.title} className={s.mile}>
              <span className={s.mileTitle}>{m.title}</span>
              <span className={s.mileAt}>{new Date(m.at).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz })} · {fmtTime(m.at, tz)}</span>
            </div>
          ))}
        </div>
      )}

      <div className={s.lanes}>
        {Object.entries(lanes).map(([id, meta]) => {
          const n = counts[id];
          const dead = !n;
          const isOff = off.has(id);
          return (
            <button
              key={id}
              type="button"
              className={`${s.laneChip} ${dead ? s.laneZero : isOff ? s.laneOff : s.laneOn}`}
              style={{ color: dead ? 'var(--ink-faint)' : LANE_COLOR[id] }}
              onClick={() => !dead && toggle(id)}
              disabled={dead}
              title={meta.blurb}
              aria-pressed={!isOff}
            >
              <span className={s.laneDot} />
              <span>{meta.label}</span>
              <span className={s.laneN}>{n === null ? 'err' : n}</span>
            </button>
          );
        })}
      </div>

      {dayList.length === 0 ? (
        <Empty title="Nothing in this window">
          No activity for this device in the last {days} days. Try a longer window.
        </Empty>
      ) : dayList.map((day) => {
        const isOpen = open.has(day);
        const sum = summaryByDay.get(day);
        const bucket = dayRows[day];
        const rows = (bucket?.events || []).filter((e) => !off.has(e.lane));
        return (
        <div key={day}>
          <button
            type="button"
            className={s.day}
            onClick={() => toggleDay(day)}
            aria-expanded={isOpen}
          >
            <span className={`${s.chev} ${isOpen ? '' : s.chevClosed}`}>
              <ChevronDown size={15} aria-hidden="true" />
            </span>
            <span className={s.dayLabel}>{fmtDayKey(day)}</span>
            <span className={s.dayRule} />
            <span className={s.dayCount}>
              {bucket?.events ? `${rows.length} events` : isOpen ? 'loading…' : 'show events'}
            </span>
          </button>

          {/* The reconciler. Server-computed, uncapped, and present whether or
              not the day's rows have been fetched — collapsed, the page IS the
              reconciliation table. */}
          {sum && <DaySummary d={sum} />}

          {isOpen && (
            bucket?.loading || !bucket ? <Loading what={`events for ${day}`} />
            : bucket.error ? <ErrorState error={bucket.error} onRetry={() => loadDay(day)} />
            : rows.length === 0 ? (
              <Empty title="No events in the selected lanes">
                This day has activity in the summary above, but none from the lanes currently shown.
              </Empty>
            ) : (
            <div className={s.rows}>
              {rows.map((e, i) => {
                const dur = duration(e.at, e.ended_at);
                const sev = e.severity === 'bad' ? s.sevBad : e.severity === 'warn' ? s.sevWarn : '';
                return (
                  <div key={`${e.source}:${e.ref}:${i}`} className={`${s.row} ${sev}`}
                       style={{ color: LANE_COLOR[e.lane] }}>
                    <span className={s.time}>{fmtTime(e.at, tz)}</span>
                    <span className={s.rail} />
                    <span className={s.lane}>{lanes[e.lane]?.label || e.lane}</span>
                    <span className={s.title}>
                      {e.title}
                      {dur && <span className={s.dur}>{dur}</span>}
                    </span>
                    <span className={s.detail} title={e.detail || ''}>{e.detail}</span>
                    <span className={s.actor} title={e.actor || ''}>{e.actor}</span>
                    <span className={s.src} title={e.source}>{e.source}</span>
                  </div>
                );
              })}
            </div>
            )
          )}
        </div>
        );
      })}

      <div className={s.foot}>
        <p style={{ margin: 0 }}>
          {shownDays} day{shownDays === 1 ? '' : 's'} over the last {days}. Days open closed and
          fetch their own rows, so the page loads the reconciliation table and nothing else.{' '}
          Plays are folded to one row per day per campaign — a device can hold tens of thousands of
          batches, and one row each would bury the handful of events that explain it.
        </p>
        {cappedLanes.length > 0 && (
          <p className={s.warnLine} style={{ margin: '6px 0 0' }}>
            Capped at {cappedAt} rows: {cappedLanes.join(', ')}. Narrow the window to see the rest.
          </p>
        )}
        {degraded?.length > 0 && (
          <p className={s.warnLine} style={{ margin: '6px 0 0' }}>
            {degraded.map((x) => `${x.lane} unavailable (${x.error})`).join(' · ')}
          </p>
        )}
      </div>
    </>
  );
}

// Every metric carries its DEFINITION, not a restatement of its label. The
// vocabulary audit found one word meaning three things across these surfaces
// ("occupied" alone is three computations); a number on a console that cannot
// say which computation it is will eventually be read as the wrong one.
const EXPLAIN = {
  plays:      'Every creative play this device recorded, from impression_batches. This is the screen\'s output, NOT what an advertiser is billed for — see Advertiser-facing.',
  billable:   'Plays that count towards an advertiser, using billing-service\'s own gate: presence_verdict = occupied, OR the play ended before the 2026-08-28 02:00 UTC cutover, OR it was already processed with money attached. This is the number Brands Manager shows.',
  occupied:   'The presence sweep confirmed a passenger aboard while the creative played. This is the denominator half of the empty rate — the two together are every play that actually got a verdict.',
  empty:      'The presence sweep found no person aboard while the creative played. Post-cutover these are voided to zero — the advertiser is not charged. The empty RATE beneath is over VERDICTED plays only (empty + aboard): it used to divide by advertiser-facing plays, which folded in pre-cutover plays that were never measured and made the rate read 0% on days nothing was verdicted (V317).',
  visionOut:  'presence_verdict = unknown: the camera could not answer, usually wedged vision. Not the same as an empty cab, and worth chasing.',
  awaiting:   'No verdict yet. The sweep runs every 15 minutes and only claims plays that ended more than 2 hours ago, so the most recent 2-3 hours are always pending. Normal, not a fault.',
  preGate:    'Plays from before the presence gate existed. The sweep only claims batches ending on or after the cutover, so these can never be verdicted — they are settled history, already invoiced.',
  rupees:     'Money attached to this device\'s batches for the day (total_amount_cents). Pre-cutover days accrued on every play; post-cutover only occupied plays accrue.',
  rides:      'Presence islands that cleared the ride floor — 120 s of covered presence, at a 600 s gap policy. Derived from raw passenger-detection evidence in the signals plane, never from identity.',
  peak:       'The most passengers seen at once during any island that day. A cab, so 1-2 is normal. This is the honest occupancy figure; the identity id count is not.',
  speech:     'Segments the speech-source model classified as live in-cab speech (not media, not Whisper hallucination). Intent matches are the rare subset that also hit a lexicon keyword.',
  commands:   'Remote commands a human issued to this device, and how many distinct operators issued them.',
  drops:      'Disconnects recorded in ggos_device_connection_history. A cab losing signal is routine; a persistent climb is not.',
  coverage:   'Share of the day\'s plays that have a presence verdict — occupied, empty or unknown. The rest are awaiting the sweep or predate the gate. Below ~90% on a finished day means the sweep is behind.',
  topCampaign:'The single campaign with the most plays on this device that day. Read it against the median: a large gap means one campaign carried the screen.',
  cpi:        'Cost per advertiser-facing impression — rupees accrued divided by billable plays. This is the number that compares to a CPM. Undefined, not zero, on a day with nothing billable.',
  cpp:        'Cost per play including plays nobody was charged for. The gap between this and CPI is what the presence gate is saving the advertiser.',
  cpm:        'Cost per mille — rupees accrued per THOUSAND advertiser-facing impressions. Every charged impression contributes to the mille, so this is the figure that compares directly to a rate card.',
  cpmAll:     'The same mille measured against EVERY play the screen made, charged or not. On a post-gate day it sits far below CPM, and the gap is exactly what the presence gate is withholding from the bill — Rs 230 against Rs 60 on 29 Aug means three quarters of the screen time went unbilled.',
  baseCpm:    'The flat rate-card CPM, Rs 109.09, derived in billing-service as (slot price 0.12 / passengers per slot 1.1) x 1000 and recorded in signals.definitions. An effective CPM near 2x base is the format multiplier — a 20 s creative prices at twice the 10 s base — not an overcharge.',
  cpur:       'Rupees divided by distinct identity profiles. Because profiles are fragmented (see Profiles reached), this is a LOWER BOUND on true cost per person — likely several times too low. Do not quote it to an advertiser.',
  reach:      'Distinct IDENTITY PROFILES that saw a creative here, resolved through merged_into. NOT a count of people: measured 2026-08-29 on this device, six rides each showing at most ONE passenger produced 4-15 profiles apiece. The merge fixes profiles that were merged; it does nothing for profiles that were never merged, and within-ride fragmentation is exactly that. Read alongside Rides.',
  profilesPerRide: 'Identity profiles divided by rides. In a cab that never shows more than one or two passengers this should be near 1. It is currently 7-9, which is the fragmentation showing — the number above is profiles, not people.',
  billablePerRide: 'Advertiser-facing plays divided by rides. On a looping in-cab screen this is high by construction — it is a delivery-intensity figure, not a frequency an advertiser should be shown.',
  trips:      'GPS trip segments, their distance, and the spread of their durations. Independent of presence — a cab can drive empty.',
  ridesPerTrip: 'Rides divided by trips. Well under 1 means the cab moved without detecting passengers: normal for deadheading, suspicious if it persists — check whether the camera is wedged.',
  speechSegments: 'Audio segments analysed that day, whatever the gate concluded. The denominator for the slices beside it.',
  speechLive: 'Segments the model classified as live in-cab speech — a person talking in the cab, not the screen and not noise. Only these are matched against the intent lexicon.',
  speechMedia:'Segments the model attributed to media playing — the screen itself, or a radio. Deliberately excluded from intent matching.',
  speechHalluc:'Segments where the transcriber produced text from road noise. A known Whisper failure on this fleet, measured at roughly 30% of field rows.',
  speechUngated:'Segments with no speech-source verdict: not yet classified, or outside the ASR tiers the model was trained on. Not a fault.',
  intents:    'Live-speech segments that also matched a keyword in the CPO lexicon. Rare by construction — the lexicon is narrow.',
  agentVersion:'The agent and player versions this device was running at the END of that day, from its last app-state telemetry. A mid-day upgrade shows as the version the day finished on.',
  battery:    'Average battery across the day\'s snapshots, with the lowest reading and the health flag the device last reported. In-cab units are usually on charge; a low floor means it was running off battery for a stretch.',
  screen:     'Brightness and media volume as the device last reported them that day, with kiosk and ADB posture. Volume 0% on an in-cab screen is deliberate, not a fault.',
  medianDist: 'med = the middle campaign; max = the largest. A day of six even campaigns and a day carried by one look identical on a total alone.',
  rangeDist:  'med = the middle duration; the range is shortest to longest.',
};

// Flags are QUESTIONS, and the tooltip says why each is worth asking and how
// it can be innocent — a flag that only accuses trains people to ignore it.
function flagExplain(f) {
  if (f.startsWith('rides recorded')) return 'The signals plane saw passengers, but no play that day counts towards an advertiser. Either the presence verdicts have not landed, or plays and rides did not overlap in time. Worth checking before anyone reports the day as delivered.';
  if (f.startsWith('drove')) return 'GPS logged three or more trips but no presence island cleared the ride floor. Innocent if the cab genuinely ran empty; suspicious if the camera was wedged or the lens was covered.';
  if (f.startsWith('moved')) return 'The cab made trips but recorded no creative plays at all. Usually a player or manifest problem rather than a perception one.';
  if (f.includes('vision unavailable')) return 'These plays were verdicted "unknown" — the camera could not answer, typically wedged vision. They are neither occupied nor empty, and they are not billed post-cutover.';
  if (f.includes('command')) return 'A remote command returned failed or error. Check the Operations lane below for which one and who issued it.';
  return undefined;
}

// A metric is a headline plus the DISTRIBUTION behind it. A day of six even
// campaigns and a day carried by one read identically on a total alone, so
// median/max sit under the total rather than in a separate table.
// The state header: the handful of facts chased on every device, from
// signals.v_device_state. Freshness is shown, not assumed — a battery reading
// with no age is a claim about now that may be a week old.
function DeviceState({ st }) {
  const yn = (v, t, f) => (v == null ? null : v ? t : f);
  const cell = (label, value, tone, title) =>
    value == null || value === '' ? null : (
      <div className={`${s.metric} ${tone === 'good' ? s.mGood : tone === 'bad' ? s.mBad
                        : tone === 'warn' ? s.mWarn : s.mDim}`} title={title}>
        <span className={`${s.mLabel} ${title ? s.mLabelHelp : ''}`}>{label}</span>
        <span className={s.mValue} style={{ fontSize: 14 }}>{value}</span>
      </div>
    );
  return (
    <div className={s.sum} style={{ marginBottom: 'var(--s5)' }}>
      <Group label="Cab">
        {cell('Cab number', st.cab_number, 'good', 'Registration of the vehicle this device is fitted to, via the active device_vehicles assignment on its inventory row.')}
        {cell('Vehicle', st.vehicle, null, 'Make and model from the vehicles register.')}
        {cell('Driver', st.driver_name, null, 'The active driver assignment for this device.')}
        {cell('Mobile', st.driver_mobile, null, 'Driver contact from the active assignment.')}
        {cell('Tenant', st.tenant_name, null, 'The tenant that owns the inventory row this device is bridged to.')}
        {cell('Where', [st.neighborhood, st.city].filter(Boolean).join(', '), null, 'Last reported location, reverse-geocoded on the device.')}
      </Group>
      <Group label="Build">
        {cell('Tablet', st.tablet_model, null, 'Hardware model reported at registration.')}
        {cell('Android', st.android_version && `${st.android_version} · OS ${st.os_version || '?'}`, null, 'Android version and GaliGali OS build.')}
        {cell('Agent', st.agent_version, null, 'Enterprise agent APK currently installed — the highest version_code, since a device can carry both release and debug packages.')}
        {cell('Player', st.player_version, null, 'Enterprise player APK currently installed.')}
      </Group>
      <Group label="Health">
        {cell('Battery', st.battery_percent != null && `${st.battery_percent}%`,
              st.battery_percent != null && st.battery_percent < 20 ? 'warn' : 'good',
              'Last reported charge. In-cab units normally run on charge.')}
        {cell('Power', yn(st.is_charging, 'charging', 'on battery'),
              st.is_charging ? 'good' : 'warn', 'Whether the device was drawing power when it last reported.')}
        {cell('Health', st.battery_health, st.battery_health === 'full' || st.battery_health === 'good' ? 'good' : 'warn',
              'Battery health as Android reports it.')}
        {cell('Network', st.network_type && `${st.network_generation || st.network_type}`,
              st.network_connected ? 'good' : 'bad',
              'Connection type and generation the device last reported.')}
        {cell('Signal', st.signal_dbm != null && `${st.signal_dbm} dBm`,
              st.signal_dbm != null && st.signal_dbm < -100 ? 'warn' : null,
              'Cell signal strength. Below about -100 dBm is a weak link and a likely cause of upload gaps.')}
        {cell('Throughput', st.throughput_down_kbps != null && `${st.throughput_down_kbps}/${st.throughput_up_kbps} kbps`,
              null, 'Down/up throughput at the last measurement.')}
      </Group>
      <Group label="Screen & posture">
        {cell('Brightness', st.brightness_percent != null && `${st.brightness_percent}%`, null,
              'Screen brightness, and whether auto-brightness is on.')}
        {cell('Volume', st.volume_percent != null && `${st.volume_percent}%`, null,
              'Media volume. 0% on an in-cab screen is deliberate — muted playback is intended, not a fault.')}
        {cell('Screen', yn(st.screen_locked, 'locked', 'awake'), st.screen_locked ? 'warn' : 'good',
              'Whether the screen was locked when the device last reported. A locked in-cab screen plays nothing.')}
        {cell('Kiosk', yn(st.kiosk_mode, 'on', 'OFF'), st.kiosk_mode ? 'good' : 'bad',
              'Kiosk mode pins the player to the foreground. Off means the screen can show something else.')}
        {cell('ADB', yn(st.adb_enabled, 'ENABLED', 'off'), st.adb_enabled ? 'warn' : 'good',
              'Android debug bridge. Enabled in the field is a security posture worth reviewing.')}
        {cell('Motion', yn(st.is_moving, 'moving', 'parked'), null,
              'Whether the device sensed motion at its last snapshot.')}
        {cell('Passenger', yn(st.passenger_aboard, 'aboard', 'none'), st.passenger_aboard ? 'good' : null,
              'Whether a presence island was open within the last two minutes.')}
      </Group>
      <Group label="Freshness">
        {cell('Snapshot', st.snapshot_at ? new Date(st.snapshot_at).toLocaleString(undefined, { timeZone: TZ }) : 'none in 7 days',
              st.snapshot_stale ? 'warn' : 'good',
              'When this state was observed. Everything above is as of this instant — the view returns nothing at all if the device has been silent for seven days.')}
        {cell('Link', st.connection_status, st.connection_status === 'online' ? 'good' : 'warn',
              'IoT Core connection status.')}
      </Group>
    </div>
  );
}

function Metric({ label, value, sub, tone, explain, dist, distExplain }) {
  if (value === null || value === undefined) return null;
  const cls = tone === 'good' ? s.mGood : tone === 'bad' ? s.mBad
            : tone === 'warn' ? s.mWarn : tone === 'dim' ? s.mDim : '';
  return (
    <div className={`${s.metric} ${cls}`} title={explain || undefined}>
      <span className={`${s.mLabel} ${explain ? s.mLabelHelp : ''}`}>{label}</span>
      <span className={s.mValue}>{value}</span>
      {sub && <span className={s.mSub}>{sub}</span>}
      {dist && <span className={s.mDist} title={distExplain}>{dist}</span>}
    </div>
  );
}

// One day, reconciled across lanes. The value of this row is not any single
// number but whether they AGREE: a cab that drove fourteen trips and recorded
// no presence is a camera fault, and no lane says so on its own.
//
// Server-computed and uncapped — summing the rendered rows would understate
// any lane sitting at its 400-row cap, which mobility routinely is.
function DaySummary({ d }) {
  const n = (v) => (v == null ? null : Number(v).toLocaleString());
  const pct = d.plays ? Math.round((d.billable / d.plays) * 100) : null;
  // "median / max across N campaigns" — the shape of the day, under its total.
  const spread = (med, max) =>
    (med == null && max == null) ? null : `med ${n(med) ?? '—'} · max ${n(max) ?? '—'}`;
  const DM = EXPLAIN.medianDist, RM = EXPLAIN.rangeDist;
  const rng = (mean, med, min, max) =>
    mean == null ? null : `med ${hhmm(med)} · ${hhmm(min)}–${hhmm(max)}`;
  const money = (v, dp = 2) =>
    v == null ? null : `₹${Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;

  return (
    <div className={s.sum}>
      {d.partial && (
        <div className={s.flags} style={{ background: 'var(--pend-soft)' }}>
          <span className={s.flag} style={{ color: 'var(--pend-text)' }}
                title="This is the oldest day in the selected window, and the window begins part-way through it. These totals cover only the part inside the window — widen the range to see the whole day.">
            partial day — window starts mid-day
          </span>
        </div>
      )}

      <Group label="Delivery">
        <Metric label="Plays" value={n(d.plays)} explain={EXPLAIN.plays}
                sub={d.campaigns ? `${d.campaigns} campaigns` : null}
                dist={spread(d.plays_median_campaign, d.plays_max_campaign)} distExplain={DM} />
        <Metric label="Advertiser-facing" value={n(d.billable)} tone="good" explain={EXPLAIN.billable}
                sub={pct != null ? `${pct}% of plays` : null}
                dist={spread(d.billable_median_campaign, d.billable_max_campaign)} distExplain={DM} />
        {/* The rate's own denominator, beside it. A percentage whose
            denominator is invisible cannot be checked, and this one was wrong
            for weeks precisely because nobody could see what it divided by. */}
        <Metric label="Passengers aboard" value={n(d.occupied)}
                tone={d.occupied ? 'good' : 'dim'} explain={EXPLAIN.occupied}
                sub={d.occupied != null && d.empty != null
                      ? `of ${n((d.occupied || 0) + (d.empty || 0))} verdicted` : null} />
        <Metric label="Empty" value={n(d.empty)} tone={d.empty ? 'warn' : 'dim'} explain={EXPLAIN.empty}
                sub={d.empty_rate_pct != null
                      ? `${d.empty_rate_pct}% of verdicted plays`
                      : 'no verdicts this day'}
                dist={spread(d.empty_median_campaign, d.empty_max_campaign)} distExplain={DM} />
        <Metric label="Vision out" value={d.vision_out ? n(d.vision_out) : null} tone="bad" explain={EXPLAIN.visionOut} />
        <Metric label="Awaiting" value={d.awaiting ? n(d.awaiting) : null} tone="dim" explain={EXPLAIN.awaiting}
                sub="verdict pending"
                dist={spread(d.awaiting_median_campaign, d.awaiting_max_campaign)} distExplain={DM} />
        <Metric label="Pre-gate" value={d.pre_gate ? n(d.pre_gate) : null} tone="dim"
                sub="never verdicted" explain={EXPLAIN.preGate} />
        <Metric label="Verdict coverage" value={d.verdict_coverage_pct != null ? `${d.verdict_coverage_pct}%` : null}
                tone="dim" explain={EXPLAIN.coverage} sub="of plays decided" />
        <Metric label="Top campaign" value={d.top_campaign_name || null} tone="dim" explain={EXPLAIN.topCampaign}
                sub={d.top_campaign_plays ? `${n(d.top_campaign_plays)} plays` : null} />
      </Group>

      <Group label="Economics">
        <Metric label="Accrued" value={money(d.rupees)} explain={EXPLAIN.rupees} />
        <Metric label="CPM" value={money(d.cpm)} tone="good" explain={EXPLAIN.cpm}
                sub={d.cpm_vs_base != null ? `${d.cpm_vs_base}× rate card` : null}
                dist={d.base_cpm_inr != null ? `base ₹${Number(d.base_cpm_inr).toFixed(2)}` : null}
                distExplain={EXPLAIN.baseCpm} />
        {/* The gate's share comes from the ratio already computed above, not
            from two ROUNDED CPMs — the same quantity either way, but the CPM
            route drifts by a point and vanishes entirely on a day with no
            billable plays, which is exactly the day an operator most wants to
            see 100% on. */}
        <Metric label="CPM · all plays" value={money(d.cpm_all_plays)} tone="dim"
                explain={EXPLAIN.cpmAll}
                sub={pct != null ? `${100 - pct}% withheld by the gate` : null} />
        <Metric label="CPI" value={money(d.cost_per_impression, 4)} explain={EXPLAIN.cpi}
                sub="per advertiser-facing play" />
        <Metric label="Cost / play" value={money(d.cost_per_play, 4)} explain={EXPLAIN.cpp}
                sub="incl. unbilled" />
        <Metric label="Cost / profile" value={money(d.cost_per_unique_reach)} tone="dim" explain={EXPLAIN.cpur}
                sub="lower bound" />
        <Metric label="Profiles reached" value={n(d.unique_reach)} tone="dim" explain={EXPLAIN.reach}
                sub={d.impressions_per_person ? `${d.impressions_per_person} impressions each` : null}
                dist={d.rides ? `${(d.unique_reach / d.rides).toFixed(1)}× per ride` : null} distExplain={EXPLAIN.profilesPerRide} />
        <Metric label="Billable / ride" value={d.billable_per_ride != null ? d.billable_per_ride : null}
                tone="dim" explain={EXPLAIN.billablePerRide} />
      </Group>

      <Group label="Passengers & movement">
        <Metric label="Rides" value={n(d.rides)} tone={d.rides ? 'good' : 'dim'} explain={EXPLAIN.rides}
                sub={d.presence_s ? `${hhmm(d.presence_s)} present` : null}
                dist={rng(d.ride_mean_s, d.ride_median_s, d.ride_min_s, d.ride_max_s)} distExplain={RM} />
        <Metric label="Peak in cab" value={d.peak_in_cab != null ? d.peak_in_cab : null}
                tone="dim" explain={EXPLAIN.peak} />
        <Metric label="Trips" value={n(d.trips)} explain={EXPLAIN.trips}
                sub={d.km != null ? `${d.km} km` : null}
                dist={rng(d.trip_mean_s, d.trip_median_s, d.trip_min_s, d.trip_max_s)} distExplain={RM} />
        <Metric label="Rides / trip" value={d.rides_per_trip != null ? d.rides_per_trip : null}
                tone={d.rides_per_trip != null && d.rides_per_trip < 0.5 ? 'warn' : 'dim'}
                explain={EXPLAIN.ridesPerTrip} />
      </Group>

      <Group label="Speech">
        <Metric label="Segments" value={n(d.speech_segments)} tone="dim" explain={EXPLAIN.speechSegments}
                sub={d.speech_language_list?.length ? d.speech_language_list.join(", ")
                     : d.speech_languages ? `${d.speech_languages} language(s)` : null} />
        <Metric label="Live" value={d.speech_live ? n(d.speech_live) : null} tone="good" explain={EXPLAIN.speechLive}
                sub={d.speech_live_pct != null ? `${d.speech_live_pct}% of segments` : null} />
        <Metric label="Media" value={d.speech_media ? n(d.speech_media) : null} tone="dim" explain={EXPLAIN.speechMedia}
                sub={d.speech_media_pct != null ? `${d.speech_media_pct}%` : null} />
        <Metric label="Hallucination" value={d.speech_hallucination ? n(d.speech_hallucination) : null}
                tone="dim" explain={EXPLAIN.speechHalluc}
                sub={d.speech_hallucination_pct != null ? `${d.speech_hallucination_pct}%` : null} />
        <Metric label="Ungated" value={d.speech_ungated ? n(d.speech_ungated) : null} tone="dim"
                explain={EXPLAIN.speechUngated} />
        <Metric label="Intents" value={d.intent_matches ? n(d.intent_matches) : null} tone="dim"
                explain={EXPLAIN.intents} />
      </Group>

      <Group label="Operations">
        <Metric label="Commands" value={d.commands ? n(d.commands) : null}
                tone={d.commands_failed ? 'bad' : undefined} explain={EXPLAIN.commands}
                sub={d.operators ? `${d.operators} operator(s)` : null}
                dist={d.commands_failed ? `${d.commands_failed} failed` : null} />
        <Metric label="Drops" value={d.drops ? n(d.drops) : null}
                tone={d.drops > 5 ? 'warn' : 'dim'} explain={EXPLAIN.drops} />
        <Metric label="Agent" value={d.agent_version || null} tone="dim" explain={EXPLAIN.agentVersion}
                sub={d.player_version ? `player ${d.player_version}` : null} />
        <Metric label="Battery" value={d.battery_avg != null ? `${d.battery_avg}%` : null}
                tone={d.battery_min != null && d.battery_min < 20 ? 'warn' : 'dim'}
                explain={EXPLAIN.battery}
                sub={d.battery_health || null}
                dist={d.battery_min != null ? `low ${d.battery_min}%${d.is_charging ? ' · charging' : ''}` : null} />
        <Metric label="Screen" value={d.brightness_percent != null ? `${d.brightness_percent}%` : null}
                tone="dim" explain={EXPLAIN.screen}
                sub={d.volume_percent != null ? `volume ${d.volume_percent}%` : null}
                dist={[d.kiosk_mode ? 'kiosk' : null, d.adb_enabled ? 'ADB on' : null]
                        .filter(Boolean).join(' · ') || null} />
      </Group>

      {d.flags?.length > 0 && (
        <div className={s.flags}>
          {d.flags.map((f) => (
            <span key={f} className={s.flag} title={flagExplain(f)}>
              <AlertTriangle size={12} aria-hidden="true" />{f}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Five groups rather than one 30-metric run-on. The grouping is the argument:
// delivery is what the screen did, economics is what it earned, passengers and
// movement are what the world did, and the last two are the machine.
function Group({ label, children }) {
  // Metric children self-suppress on a null value; the state header's cells
  // already return null when empty. Only filter the former, or the latter get
  // dropped wholesale and the group renders as an empty rule.
  const kids = React.Children.toArray(children).filter(Boolean);
  const shown = kids.filter((k) => !k.props || k.props.value === undefined || k.props.value != null);
  if (!shown.length) return null;
  return (
    <div className={s.group}>
      <span className={s.groupLabel}>{label}</span>
      <div className={s.groupItems}>{shown}</div>
    </div>
  );
}
